import React, { useState, useEffect } from "react";
import {
    FormGroup,
    Label,
    Input,
    Alert,
} from "reactstrap";
import { LoadingButton } from "../../../../components/Common";
import { INTEGRATION_PROVIDERS } from "../constants";

/**
 * IntegrationDetail
 * Painel direito do Master-Detail: formulário de configuração + área de teste.
 */
const STATUS_CONFIG = {
    open: { label: "Online", color: "#34c759", icon: "mdi-check-circle" },
    close: { label: "Offline", color: "#ff3b30", icon: "mdi-close-circle" },
    connecting: { label: "Conectando...", color: "#ff9500", icon: "mdi-loading mdi-spin" },
    checking: { label: "Verificando...", color: "#8e8e93", icon: "mdi-loading mdi-spin" },
    error: { label: "Erro", color: "#ff3b30", icon: "mdi-alert-circle" },
};

const IntegrationDetail = ({
    providerId,
    providerData,
    onSave,
    isSaving,
    onTest,
    isTesting,
    testResult,
    onClearTest,
    evolutionStatus,
}) => {
    const provider = INTEGRATION_PROVIDERS.find((p) => p.id === providerId);
    const [formData, setFormData] = useState({});
    const [testInput, setTestInput] = useState("");

    // Inicializa o form quando muda de provider
    useEffect(() => {
        if (providerData) {
            setFormData({
                provider: providerData.provider,
                name: providerData.name || provider?.name || "",
                isActive: providerData.isActive || false,
                config: { ...(providerData.config || {}) },
                id: providerData.id || null,
                createdAt: providerData.createdAt || null,
                createdBy: providerData.createdBy || null,
            });
        }
        setTestInput("");
        if (onClearTest) onClearTest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providerId, providerData]);

    if (!provider) {
        return (
            <div className="border border-top rounded-0 h-100 overflow-hidden bg-white">
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ minHeight: "400px" }}
                >
                    <div className="text-center text-muted">
                        <i className="mdi mdi-arrow-left-bold font-size-48 opacity-25 mb-3 d-block"></i>
                        <p className="font-size-14 mb-0">
                            Selecione uma integração para configurar.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const handleConfigChange = (key, value) => {
        setFormData((prev) => ({
            ...prev,
            config: { ...prev.config, [key]: value },
        }));
    };

    const handleSubmit = () => {
        onSave(formData);
    };

    const handleTestClick = () => {
        onTest(providerId, testInput);
    };

    return (
        <div className="border border-top rounded-0 h-100 overflow-hidden bg-white">
            {/* Header */}
            <div className="bg-white border-bottom py-3 px-4">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <i
                            className={`mdi ${provider.icon} font-size-24 me-3`}
                            style={{ color: provider.color }}
                        ></i>
                        <div>
                            <h6 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                {provider.name}
                                {providerId === "evolution" && evolutionStatus && STATUS_CONFIG[evolutionStatus] && (
                                    <span
                                        className="d-inline-flex align-items-center gap-1 rounded-0 px-2 py-0"
                                        style={{
                                            fontSize: "11px",
                                            fontWeight: 600,
                                            color: STATUS_CONFIG[evolutionStatus].color,
                                            backgroundColor: `${STATUS_CONFIG[evolutionStatus].color}15`,
                                            border: `1px solid ${STATUS_CONFIG[evolutionStatus].color}30`,
                                        }}
                                    >
                                        <i className={`mdi ${STATUS_CONFIG[evolutionStatus].icon}`} style={{ fontSize: "12px" }}></i>
                                        {STATUS_CONFIG[evolutionStatus].label}
                                    </span>
                                )}
                            </h6>
                            <p className="text-muted small mb-0">
                                {provider.description}
                            </p>
                        </div>
                    </div>

                    {/* Switch ativar/desativar */}
                    <div className="d-flex align-items-center gap-2">
                        <span
                            className={`font-size-12 fw-semibold ${
                                formData.isActive ? "text-success" : "text-muted"
                            }`}
                        >
                            {formData.isActive ? "Ativo" : "Inativo"}
                        </span>
                        <div className="form-check form-switch mb-0">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                checked={formData.isActive || false}
                                onChange={() =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        isActive: !prev.isActive,
                                    }))
                                }
                                style={{
                                    cursor: "pointer",
                                    width: "2.5em",
                                    height: "1.2em",
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-4 overflow-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                {/* Campos de configuração */}
                <h6 className="fw-bold font-size-14 mb-3 text-dark">
                    <i className="mdi mdi-cog-outline me-2 text-primary"></i>
                    Configuração
                </h6>

                {provider.configFields.map((field) => (
                    <FormGroup key={field.key} className="mb-3">
                        <Label className="fw-semibold font-size-12 text-muted text-uppercase mb-2">
                            {field.label}
                            {field.required && (
                                <span className="text-danger ms-1">*</span>
                            )}
                        </Label>

                        {field.type === "select" ? (
                            <Input
                                type="select"
                                value={formData.config?.[field.key] || ""}
                                onChange={(e) =>
                                    handleConfigChange(field.key, e.target.value)
                                }
                                className="rounded-0 py-2 border"
                            >
                                {field.options.map((opt) => (
                                    <option key={opt} value={opt}>
                                        {opt}
                                    </option>
                                ))}
                            </Input>
                        ) : field.type === "textarea" ? (
                            <Input
                                type="textarea"
                                rows="3"
                                placeholder={field.placeholder}
                                value={formData.config?.[field.key] || ""}
                                onChange={(e) =>
                                    handleConfigChange(field.key, e.target.value)
                                }
                                className="rounded-0 py-2 border"
                            />
                        ) : (
                            <Input
                                type={field.type || "text"}
                                placeholder={field.placeholder}
                                value={formData.config?.[field.key] || ""}
                                onChange={(e) =>
                                    handleConfigChange(field.key, e.target.value)
                                }
                                className="rounded-0 py-2 border"
                            />
                        )}
                    </FormGroup>
                ))}

                {/* Botão Salvar */}
                <div className="d-flex justify-content-end pt-3 border-top mb-4">
                    <LoadingButton
                        color="primary"
                        className="rounded-0 px-4"
                        loading={isSaving}
                        onClick={handleSubmit}
                        loadingText="Salvando..."
                    >
                        Salvar
                    </LoadingButton>
                </div>

                {/* Área de Teste */}
                <div className="border-top pt-4 mt-2">
                    <h6 className="fw-bold font-size-14 mb-3 text-dark">
                        <i className="mdi mdi-flask-outline me-2 text-warning"></i>
                        Testar Integração
                    </h6>
                    <p className="text-muted small mb-3">
                        {providerId === "evolution"
                            ? "Digite um número de WhatsApp para enviar uma mensagem de teste. O status da conexão é monitorado automaticamente."
                            : "Envia uma mensagem de teste para verificar se a API Key e o modelo estão funcionando."}
                    </p>

                    <div className="d-flex gap-2 align-items-start">
                        <Input
                            type="text"
                            className="rounded-0 py-2 border flex-grow-1"
                            placeholder={provider.testPlaceholder}
                            value={testInput}
                            onChange={(e) => setTestInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleTestClick();
                            }}
                        />
                        <LoadingButton
                            color="warning"
                            className="rounded-0 text-white flex-shrink-0"
                            loading={isTesting}
                            onClick={handleTestClick}
                            loadingText="Testando..."
                        >
                            <i className="mdi mdi-play-circle-outline me-1"></i>
                            Testar
                        </LoadingButton>
                    </div>

                    {/* Resultado do teste */}
                    {testResult && (
                        <Alert
                            color={testResult.success ? "success" : "danger"}
                            className="mt-3 mb-3 font-size-13"
                            style={{ whiteSpace: "pre-wrap" }}
                            fade={false}
                        >
                            <div className="d-flex align-items-start">
                                <i
                                    className={`mdi ${
                                        testResult.success
                                            ? "mdi-check-circle-outline text-success"
                                            : "mdi-alert-circle-outline text-danger"
                                    } font-size-18 me-2 mt-1 flex-shrink-0`}
                                ></i>
                                <div style={{ maxHeight: "200px", overflow: "auto" }}>
                                    {testResult.message}
                                </div>
                            </div>
                        </Alert>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IntegrationDetail;
